import { IEvent, IReoEncryptKey } from '@reolink-cloud/interface';
import { DateTime, Storage } from '@reolink-web/utils';

export const LIBRARY_NAVIGATION_QUERY = 'libraryContext';
const STORAGE_PREFIX = 'reolink-library-navigation:';
const CONTEXT_TTL = 6 * DateTime.MS_PER_HOUR;
const AI_POSITION_MAX_TOTAL = 79;

export type LibraryNavigationSource = 'filter' | 'ai';

export interface ILibraryNavigationItem {

    id: string;
    sequence?: number;
    cid?: string;
}

export interface ILibraryNavigationContext {

    version: 1;
    createdAt: number;
    source: LibraryNavigationSource;
    items: ILibraryNavigationItem[];
    totalRows: number;
    nextToken: string | null;
}

interface ILibraryNavigationStorage {

    context: ILibraryNavigationContext;
}

interface EventListPage {
    items: IEvent[];
    totalRows: number;
    nextToken: string | null;
    encryptKeys?: Record<string, IReoEncryptKey>;
}

function createNavigationStorage (
    id: string
): Storage.WebStorage<ILibraryNavigationStorage> {

    return new Storage.WebStorage<ILibraryNavigationStorage>({
        type: 'sessionStorage',
        keys: ['context'],
        defaultOptions: {
            prefix: `${STORAGE_PREFIX}${id}_`
        }
    });

}

function getEventCid (
    event: IEvent,
    encryptKeys: Record<string, IReoEncryptKey> = {}
): string | undefined {

    for ( const gallery of event.items ?? [] ) {

        const keyId = gallery.encrypt?.keyId;
        if ( keyId && keyId !== '0' && encryptKeys[keyId]?.cmkId ) {

            return encryptKeys[keyId].cmkId;

        }

    }
    return undefined;

}

export function createLibraryNavigationId (): string {

    const randomId = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString( 16 ).slice( 2 )}`;
    return randomId;

}

export function createLibraryNavigationContext (
    source: LibraryNavigationSource,
    page: EventListPage
): ILibraryNavigationContext {

    return appendLibraryNavigationPage({
        version: 1,
        createdAt: Date.now(),
        source,
        items: [],
        totalRows: page.totalRows,
        nextToken: page.nextToken
    }, page );

}

export function appendLibraryNavigationPage (
    context: ILibraryNavigationContext,
    page: EventListPage
): ILibraryNavigationContext {

    const knownIds = new Set( context.items.map( item => item.id ));
    const appendedItems = page.items.reduce<ILibraryNavigationItem[]>(( items, event ) => {

        if ( knownIds.has( event.id )) return items;
        knownIds.add( event.id );
        items.push({
            id: event.id,
            sequence: event.sequence,
            cid: getEventCid( event, page.encryptKeys )
        });
        return items;

    }, [] );

    return {
        ...context,
        items: context.items.concat( appendedItems ),
        totalRows: page.totalRows >= 0 ? page.totalRows : context.totalRows,
        nextToken: page.nextToken
    };

}

export function saveLibraryNavigationContext (
    id: string,
    context: ILibraryNavigationContext
): void {

    if ( !id ) return;
    try {

        createNavigationStorage( id ).setItem( 'context', context );

    } catch ( error ) {

        console.warn( '[library navigation] failed to save context', error );

    }

}

export function readLibraryNavigationContext ( id: string ): ILibraryNavigationContext | null {

    if ( !id ) return null;
    try {

        // The token is opaque; sanitizing the serialized context can change it.
        const context = createNavigationStorage( id ).getItem( 'context', {
            shouldXssFilter: false
        });
        if ( !context ) return null;
        const isValid = context.version === 1
            && Array.isArray( context.items )
            && ( context.source === 'filter' || context.source === 'ai' )
            && Date.now() - context.createdAt <= CONTEXT_TTL;
        if ( !isValid ) {

            createNavigationStorage( id ).removeItem( 'context' );
            return null;

        }
        return context;

    } catch ( error ) {

        console.warn( '[library navigation] failed to read context', error );
        return null;

    }

}

export function removeLibraryNavigationContext ( id: string ): void {

    if ( !id ) return;
    try {

        createNavigationStorage( id ).removeItem( 'context' );

    } catch ( error ) {

        console.warn( '[library navigation] failed to remove context', error );

    }

}

export function getLibraryNavigationPosition (
    context: ILibraryNavigationContext,
    eventId: string
): number {

    const index = context.items.findIndex( item => item.id === eventId );
    if ( index < 0 ) return 0;
    const sequence = context.items[index].sequence;
    return context.source === 'filter' && typeof sequence === 'number'
        ? sequence
        : index + 1;

}

export function shouldShowLibraryNavigationPosition (
    context: ILibraryNavigationContext
): boolean {

    return context.totalRows > 0
        && ( context.source === 'filter' || context.totalRows <= AI_POSITION_MAX_TOTAL );

}
